import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function TeacherSpotlightCard({ teacher, compact = false }) {
  const { t } = useTranslation();
  const user = teacher.user || {};
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || t('Teacher');
  const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}` || 'T';
  const description = teacher.bio || teacher.qualifications || t('Discover this teacher’s courses and learning resources.');

  return (
    <Link
      to={`/teachers/${user.id}`}
      className={`group flex h-full flex-col items-center rounded-3xl border border-surface-200 bg-white text-center shadow-card transition duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-card-hover ${compact ? 'px-5 py-7' : 'px-6 py-9'}`}
      aria-label={t('View {{name}}’s profile', { name })}
    >
      <div className="relative">
        <div className={`${compact ? 'h-24 w-24' : 'h-28 w-28'} overflow-hidden rounded-full bg-gradient-to-br from-primary-100 to-accent-100 p-1 ring-4 ring-primary-50 transition group-hover:ring-primary-100`}>
          {user.profile_picture ? (
            <img src={user.profile_picture} alt={name} className="h-full w-full rounded-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-accent-500 text-2xl font-bold text-white">
              {initials.toUpperCase()}
            </div>
          )}
        </div>
        {teacher.is_featured && (
          <span className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full border-4 border-white bg-amber-400 text-white shadow-sm" title={t('Featured teacher')}>
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.75l2.73 5.53 6.1.89-4.42 4.3 1.04 6.08L12 16.68l-5.45 2.87 1.04-6.08-4.42-4.3 6.1-.89L12 2.75z" /></svg>
          </span>
        )}
      </div>
      <h3 className="mt-5 font-display text-lg font-bold text-gray-900 group-hover:text-primary-700">{name}</h3>
      {teacher.specializations?.length > 0 && (
        <div className="mt-2 flex flex-wrap justify-center gap-1.5">
          {teacher.specializations.slice(0, 2).map((item) => (
            <span key={item} className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">{item}</span>
          ))}
        </div>
      )}
      <p className="mt-3 line-clamp-3 text-sm leading-6 text-gray-500">{description}</p>
      <span className="mt-auto pt-5 text-sm font-semibold text-primary-600">
        {t('View profile')} <span aria-hidden="true" className="inline-block transition-transform group-hover:translate-x-1 rtl:rotate-180">→</span>
      </span>
    </Link>
  );
}
